import { dlog, dLogTime, dLogTimeEnd } from "./utility.js";
import { debugDesc } from "./utility.js";
import { notificationCenter } from "@shared/NotificationCenter.js";
import { Notifications } from "@shared/Notifications.js";
import { currentDatabaseName, getCurrentServerEnv, ServerEnv } from "@shared/environment.js";
import { initializeApp } from "firebase/app";
import { getAuth, signInAnonymously } from "firebase/auth";
import {
    getFirestore,
    connectFirestoreEmulator,
    doc,
    getDoc as getDocOriginal,
    getDocs as getDocsOriginal,
    updateDoc,
    addDoc,
    setDoc,
    collection,
    increment,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, logEvent as firebaseLogEvent } from "firebase/analytics";

class FirebaseManager {
    static authStateDidChangeNotification = "firebaseAuthStateChanged";

    constructor() {
        dlog(">>> FirebaseManager");

        this.firebaseConfig = {
            apiKey: "AIzaSyC7vYFfbDh8L4MbyqNt6pscA4L6D48o-w8",
            authDomain: "ai-gallery-60cd7.firebaseapp.com",
            projectId: "ai-gallery-60cd7",
            storageBucket: "ai-gallery-60cd7.appspot.com",
            messagingSenderId: "546322937580",
            appId: "1:546322937580:web:80750e98e853ac5e69d7c9",
            measurementId: "G-116442XREW"
        };
        this.app = initializeApp(this.firebaseConfig);
        this.auth = getAuth();
        this.auth.useDeviceLanguage();
        this.db = this.initializeDatabase();
        this.storage = getStorage();
        this.functions = getFunctions();
        this.itemCntIncs = new Map();

        // Initialize analytics only in production
        this.analytics = getCurrentServerEnv() === ServerEnv.prod ? getAnalytics(this.app) : null;

        // Cache for signed URLs with configurable expiration
        this.signedURLCache = new Map();
        this.cacheExpirationMinutes = 60; // Default to 60 minutes, same as signed URL expiration

        // Firestore read counter
        this.firestoreReadCount = 0;
        
        this.auth.onAuthStateChanged(user => {
            dlog("Auth state changed; user:" + debugDesc(user));
            if (user) {
                notificationCenter.post(FirebaseManager.authStateDidChangeNotification, user);
            } else {
                // will retrigger this listener
                this.signInToFirebaseAnonymously();
            }
        });
    }
    
    initializeDatabase() {
        const dbName = currentDatabaseName();
        dlog(`Initializing database: ${dbName}`);
        
        if (dbName === "(default)") {
            return getFirestore(this.app);
        } else {
            return getFirestore(this.app, dbName);
        }
    }

    async incAssetCount(assetID, field) {
        // Only count one view per asset per session (addToCartCnt is not deduplicated
        // because each add-to-cart is a distinct event worth counting)
        if (field === "viewCnt" && this.itemCntIncs.get(assetID) != null) {
            dlog("suppressing incAssetCount viewCnt on " + assetID);
            return;
        }
        try {
            dlog("incAssetCount start: " + field + " on " + assetID);

            await this.withRetry(async () => {
                const docRef = doc(this.db, "asset-counts", assetID);
                await setDoc(docRef, { [field]: increment(1), lastModified: new Date().toISOString() }, { merge: true });
            });

            if (field === "viewCnt") {
                this.itemCntIncs.set(assetID, true);
            }
            dlog("incAssetCount done: " + field + " on " + assetID);
        } catch (error) {
            console.error("Error incrementing asset count after retries: ", error);
        }
    }
    
    async createDesignAsset(assetID, assetType, interactionType, productCat, productSKU) {
        try {
            await this.withRetry(async () => {
                const colRef = collection(this.db, "design-assets");
                await addDoc(colRef, {
                    created_at: new Date().toISOString(),
                    assetID: assetID,
                    productCat: productCat,
                    productSKU: productSKU,
                    assetType: assetType,
                    interactionType: interactionType,
                });
            });
            if (interactionType === "addedToCart") {
                this.incAssetCount(assetID, "addToCartCnt");
            }
            dlog("createDesignAsset done: " + assetType + " / " + interactionType);
        } catch (error) {
            console.error("Error creating design asset after retries: ", error);
        }
    }

    async signInToFirebaseAnonymously() {
        dLogTime("signInToFirebaseAnonymously");
        dlog("signInToFirebaseAnonymously");
        
        try {
            await signInAnonymously(this.auth);
            dlog("User signed in anonymously");
            dLogTimeEnd("signInToFirebaseAnonymously");
        } catch (error) {
            var errorCode = error.code;
            var errorMessage = error.message;
            console.error("Error during anonymous sign-in:", errorCode, errorMessage);
            throw error; // Re-throw so the retry logic can handle it
        }
    }
    
    // Configure cache expiration time (in minutes)
    setCacheExpirationMinutes(minutes) {
        this.cacheExpirationMinutes = minutes;
    }

    // Clear the entire cache
    clearSignedURLCache() {
        this.signedURLCache.clear();
    }

    // Clear a specific URL from cache
    clearCachedURL(path) {
        this.signedURLCache.delete(path);
    }
    
    async withRetry(operation, maxAttempts = 10) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await operation();
            } catch (error) {
                dlog(`Firebase operation failed (attempt ${attempt}/${maxAttempts}): ${error.message}`);

                if (attempt === maxAttempts) {
                    console.warn(`Firebase operation failed after ${maxAttempts} attempts:`, error);
                    throw error;
                }

                dlog("Attempting re-authentication...");
                try {
                    await this.signInToFirebaseAnonymously();
                } catch (authError) {
                    console.warn("Re-authentication failed:", authError);
                }

                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    }

    // Firestore read tracking methods
    incrementReadCount(count = 1) {
        this.firestoreReadCount += count;
        //dlog(`Firestore read count: ${this.firestoreReadCount}`);
    }

    getReadCount() {
        return this.firestoreReadCount;
    }

    resetReadCount() {
        this.firestoreReadCount = 0;
        //dlog("Firestore read count reset to 0");
    }

    printReadCount() {
        //dlog(`Total Firestore reads: ${this.firestoreReadCount}`);
        return this.firestoreReadCount;
    }

    logEvent(eventName, params = null) {
        if (this.analytics) {
            if (params) {
                firebaseLogEvent(this.analytics, eventName, params);
            } else {
                firebaseLogEvent(this.analytics, eventName);
            }
        }
    }

}

let firebaseShared = null;

function initFirebase() {
    if (firebaseShared) return;
    firebaseShared = new FirebaseManager();

    if (typeof window !== "undefined") {
        window.printFirestoreReads = () => firebaseShared.printReadCount();
        window.resetFirestoreReads = () => firebaseShared.resetReadCount();
    }
}

notificationCenter.observe(Notifications.appWillBecomeActive, initFirebase);

async function getDocTracked(docRef) {
    const result = await getDocOriginal(docRef);
    if (result.exists() && firebaseShared) {
        firebaseShared.incrementReadCount(1);
    }
    return result;
}

async function getDocsTracked(collectionRef) {
    const result = await getDocsOriginal(collectionRef);
    if (firebaseShared) {
        const readCount = result.size;
        firebaseShared.incrementReadCount(readCount);
    }
    return result;
}

function logEvent(eventName, params = null) {
    if (!firebaseShared) return;
    firebaseShared.logEvent(eventName, params);
}

export {
    FirebaseManager,
    firebaseShared,
    initFirebase,
    signInAnonymously,
    doc,
    getDocTracked as getDoc,
    getDocsTracked as getDocs,
    collection,
    ref,
    logEvent
};

